import { DialogButton } from '@beeinventor/dasiot-react-component-lib';
import { CircularProgress, Popover, styled } from '@mui/material';
import { Outlet } from 'react-router-dom';

import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getTasks } from '../../apis/tasksApi';
import AddSvgIcon from '../../components/SvgIcon/AddSvgIcon';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { setTaskFilter } from '../../slices/systemSlice';
import Header from './Header';
import TaskItem from './TaskItem';
import { ProjectMap } from '../../types/Project';
import { getProjects } from '../../apis/projectApi';
import CreateTaskDialog from './create-task/CreateTaskDialog';
import { endOfDay, startOfDay } from 'date-fns';
import { getDasEndpoints } from '../../apis/dasEndpointApi';

const Container = styled('div')`
  display: flex;
  flex-direction: column;
`;

const Content = styled('div')(() => {
  return {
    height: 'calc(100vh - 74px - 74px)',
    '& > .add-container': {
      textAlign: 'center',
      padding: '24px',
    },
    '& > .header-item': {
      padding: '0 48px ',
      marginLeft: '4px',
      '& > .container-header': {
        padding: '11px 24px',
        width: '100%',
        display: 'grid',
        alignItems: 'center',
        'grid-template-columns': '40px 1fr 1fr 1fr 80px 150px 150px 80px 32px',
      },
    },

    '& > .list-container': {
      height: 'calc(100% - 40px)',
      padding: '0 48px',
      minWidth: '500px',
      overflow: 'auto',
    },

    '.loading-container': {
      display: 'flex',
      justifyContent: 'center',
    },

    '.loader': {
      height: '20px',
    },
  };
});

const TaskListPage = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const listRef = useRef<HTMLDivElement | null>(null);
  const loader = useRef<HTMLDivElement | null>(null);
  const [inProgress, setInProgress] = useState<boolean>(false);
  const { projectId, createDate, taskType } = useAppSelector(
    (store) => store.system.filter.task,
  );

  const filterText = useAppSelector((store) => store.system.filter.text);

  const { data: filteredDevices } = useQuery({
    queryKey: ['filter-devices', filterText],
    queryFn: async () => {
      if (!filterText.trim()) return [];
      const response = await getDasEndpoints({ keyword: filterText });
      return response.data.data;
    },
    enabled: filterText.trim().length > 0,
  });

  const { data: queryResult, isFetched } = useQuery({
    queryKey: ['get-project-map'],
    queryFn: async () => {
      const res = await getProjects();
      const projectMap = res.data.data.reduce<ProjectMap>((prev, curr) => {
        prev[curr.id] = curr;
        return prev;
      }, {});
      const LIST_PROJECT = res.data.data.map((project) => ({
        id: project.id,
        name: project.name,
        value: project.id,
      }));
      return { projectMap, LIST_PROJECT }; // Return an object instead of array
    },
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  // Safely access the data with default values
  const projectMap = queryResult?.projectMap ?? {};
  const LIST_PROJECT = queryResult?.LIST_PROJECT ?? [];

  const {
    data: tasks,
    refetch: refetchTask,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading,
  } = useInfiniteQuery({
    queryKey: ['get-tasks', projectId, taskType, createDate],
    queryFn: async ({ pageParam }) => {
      const res = await getTasks({
        type: taskType === 'all' ? undefined : taskType,
        nextCursor: pageParam as string | undefined,
        projectId,
      });
      return res;
    },
    getNextPageParam: (lastPage: any) => {
      return lastPage.data.paging.nextCursor;
    },
    enabled: location.pathname === '/task-list',
    refetchOnWindowFocus: false,
    initialPageParam: undefined,
    refetchInterval: inProgress ? 60 * 1000 : false, // auto refetch while exist task inprogress
    staleTime: 1000 * 1000, //cache result when change page
  });

  const handleObserver = useCallback(
    (entries: any[]) => {
      const target = entries[0];
      if (target.isIntersecting && hasNextPage) {
        fetchNextPage();
      }
    },
    [hasNextPage, fetchNextPage],
  );

  useEffect(() => {
    return () => {
      dispatch(
        setTaskFilter({
          createDate: null,
          projectId: undefined,
          taskType: 'all',
        }),
      );
    };
  }, []);

  useEffect(() => {
    const option = {
      root: listRef.current,
      rootMargin: '-5px',
      threshold: 0.5,
    };
    const observer = new IntersectionObserver(handleObserver, option);
    if (loader.current) {
      observer.observe(loader.current);
    }

    return () => {
      if (loader.current) {
        observer.unobserve(loader.current);
      }
    };
  }, [handleObserver]);

  const simpleTaskComponents = useMemo(() => {
    // check whether inProgress task exist

    setInProgress(
      tasks?.pages[0].data.data.filter((d) => d.status === 'inProgress')
        .length !== 0,
    );

    return tasks?.pages
      .map((res) => res.data.data)
      .flat()
      .filter((task) => {
        const matchedDasId =
          filterText.trim() !== '' &&
          task.tasks.some((subtask) =>
            filteredDevices?.some((device) => subtask.endpointId === device.id),
          );

        const matchesFilterText =
          filterText.trim() !== '' &&
          (task.name
            .toLocaleLowerCase()
            .includes(filterText.toLocaleLowerCase()) ||
            task.content
              .toLocaleLowerCase()
              .includes(filterText.toLocaleLowerCase()) ||
            task.status
              .toLocaleLowerCase()
              .includes(filterText.toLocaleLowerCase()));

        // type filter
        const matchesTaskType = task.type === taskType || taskType === 'all';
        // time filter
        const matchDateInterval = createDate
          ? new Date(task.createdAt) >= startOfDay(new Date(createDate)) &&
            new Date(task.createdAt) <= endOfDay(new Date(createDate))
          : true;
        return (
          (filterText.trim() === '' || matchesFilterText || matchedDasId) &&
          matchesTaskType &&
          matchDateInterval
        );
      })
      .map((task) => (
        <TaskItem
          key={`simple-task-ticket-${task.id}`}
          taskGroup={task}
          projectMap={projectMap}
        />
      ));
  }, [tasks, isFetched, filterText, filteredDevices]);

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  return (
    <>
      <Container>
        <Header />
        <Content className="content">
          <div className="add-container">
            <DialogButton
              sx={{
                fontWeight: '500',
                padding: '0 16px 0 32px',
              }}
              variant="contained"
              color="primary"
              endIcon={<AddSvgIcon sx={{ width: '32px', height: '32px' }} />}
              onClick={handleClick}
            >
              Add Task
            </DialogButton>
            {/* <Input onChange={optimisedSearchHandler} /> */}
            <Popover
              id={id}
              open={open}
              anchorEl={anchorEl}
              onClose={handleClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
            >
              <CreateTaskDialog
                handleClose={handleClose}
                handleTaskListRefetch={refetchTask}
                LIST_PROJECT={LIST_PROJECT}
              />
            </Popover>
          </div>
          <div className="header-item">
            <div className="container-header">
              <div />
              <div>Type</div>
              <div>Name</div>
              <div>Project</div>
              <div>item</div>
              <div>Create Date</div>
              <div>Finish Date</div>
              <div>Status</div>
              <div />
            </div>
          </div>
          <div ref={listRef} className="list-container">
            {isLoading ? (
              <div className="loading-container">
                <CircularProgress color="primary" />
              </div>
            ) : (
              <>
                {simpleTaskComponents}
                {isFetching && isFetchingNextPage && (
                  <div className="loading-container">
                    <CircularProgress color="primary" />
                  </div>
                )}
                <div ref={loader} className="loader" />
              </>
            )}
          </div>
        </Content>
      </Container>
      <Outlet />
    </>
  );
};

export default TaskListPage;
